import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen, Mic, PenLine, Headphones, Award, TrendingUp, ChevronRight, Crown } from 'lucide-react';

// Brand assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_spanenglishprep/artifacts/i0n29hu3_Exam%20Ready%20English%20Logo.png";
const COVER_URL = "https://customer-assets.emergentagent.com/job_spanenglishprep/artifacts/795ln30m_exam%20ready%20english%20cover.png";

export default function LandingPage() {
  const navigate = useNavigate();
  
  // HeyGen disabled - no credits available
  const HEYGEN_DISABLED = true;

  const features = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Reading & Use of English",
      titleEs: "Lectura y Uso del Inglés",
      description: "Practice with authentic Cambridge-style texts and exercises"
    },
    {
      icon: <PenLine className="w-6 h-6" />,
      title: "Writing with AI Feedback",
      titleEs: "Escritura con Feedback de IA",
      description: "Get personalized corrections and improvement suggestions"
    },
    {
      icon: <Headphones className="w-6 h-6" />,
      title: "Listening Practice",
      titleEs: "Práctica de Listening",
      description: "Develop your comprehension with professional audio content"
    },
    {
      icon: <Mic className="w-6 h-6" />,
      title: "AI Speaking Partner",
      titleEs: "Compañero de Speaking con IA",
      description: "Practice conversations with an intelligent examiner simulation"
    }
  ];

  const levels = ["B1 Preliminary", "B2 First", "C1 Advanced", "C2 Proficiency"];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Exam Ready English" className="h-10 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/blog')}
                data-testid="nav-blog-btn"
                className="text-slate-600 hover:text-slate-900"
              >
                Blog
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                data-testid="nav-login-btn"
                className="text-slate-600 hover:text-slate-900"
              >
                Iniciar Sesión
              </Button>
              <Button 
                onClick={() => navigate('/auth?mode=register')}
                data-testid="nav-register-btn"
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6"
              >
                Comenzar Gratis
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
              <p className="text-xs uppercase tracking-widest text-orange-600 font-semibold mb-4">
                Para Profesionales de Habla Hispana
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
                <span className="text-orange-600">Exam Ready</span> English
                <br />
                <span className="text-3xl sm:text-4xl lg:text-5xl text-slate-700">Cambridge Exam Practice</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-xl">
                La plataforma diseñada específicamente para profesionales de habla hispana que quieren certificar su nivel de inglés. Ejercicios auténticos, feedback con IA y seguimiento de progreso.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/auth?mode=register')}
                  data-testid="hero-cta-btn"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105"
                >
                  Empezar Ahora
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/auth')}
                  data-testid="hero-login-btn"
                  className="border-2 border-slate-900 text-slate-900 hover:bg-slate-50 rounded-full px-8 py-6 text-lg"
                >
                  Ya tengo cuenta
                </Button>
              </div>
              
              {/* Levels */}
              <div className="mt-12 flex flex-wrap gap-3">
                {levels.map((level, i) => (
                  <span 
                    key={level}
                    className="px-4 py-2 bg-white border border-slate-200 text-sm text-slate-600 rounded-sm"
                  >
                    {level}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="lg:col-span-5 animate-fade-in-up animation-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-slate-900 to-slate-800 rounded-sm transform rotate-3"></div>
                <img 
                  src={COVER_URL}
                  alt="Exam Ready English - Graduates celebrating"
                  className="relative w-full h-[400px] object-cover rounded-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <p className="text-xs uppercase tracking-widest text-orange-600 font-semibold mb-4">
              Todo lo que necesitas
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Practica todas las secciones del examen
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="p-6 bg-[#FDFBF7] border border-slate-200 hover:shadow-lg transition-all duration-300 animate-fade-in-up group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-slate-900 text-white rounded-sm flex items-center justify-center mb-4 group-hover:bg-orange-600 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                <p className="text-sm text-orange-600 mb-2">{feature.titleEs}</p>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Examiner Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Video Section */}
            <div className="order-2 lg:order-1 flex justify-center">
              <div className="relative">
                {/* Decorative background */}
                <div className="absolute -inset-4 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl transform -rotate-3 opacity-20"></div>
                
                {/* Avatar Container - Static placeholder */}
                <div className="relative w-72 h-72 sm:w-80 sm:h-80 bg-slate-900 rounded-2xl overflow-hidden shadow-2xl">
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
                    {/* Animated avatar circle */}
                    <div className="relative mb-4">
                      <div className="w-28 h-28 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center shadow-xl">
                        <span className="text-5xl font-bold text-white">C</span>
                      </div>
                      {/* Pulse animation */}
                      <div className="absolute inset-0 w-28 h-28 rounded-full bg-orange-400 animate-ping opacity-20"></div>
                    </div>
                    <span className="text-xl text-white font-semibold">Jane</span>
                    <span className="text-slate-400 mt-1">Tu Examinadora AI</span>
                    {/* Speaking indicator bars */}
                    <div className="flex items-center gap-1.5 mt-4">
                      <div className="w-2 h-4 bg-green-400 rounded-full animate-pulse"></div>
                      <div className="w-2 h-6 bg-green-400 rounded-full animate-pulse" style={{animationDelay: '150ms'}}></div>
                      <div className="w-2 h-3 bg-green-400 rounded-full animate-pulse" style={{animationDelay: '300ms'}}></div>
                      <div className="w-2 h-7 bg-green-400 rounded-full animate-pulse" style={{animationDelay: '450ms'}}></div>
                      <div className="w-2 h-4 bg-green-400 rounded-full animate-pulse" style={{animationDelay: '600ms'}}></div>
                    </div>
                  </div>
                </div>
                
                {/* Floating badge */}
                <div className="absolute -top-3 -right-3 bg-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                  IA Avatar
                </div>
              </div>
            </div>
            
            {/* Text Content */}
            <div className="order-1 lg:order-2 animate-fade-in-up">
              <p className="text-xs uppercase tracking-widest text-orange-600 font-semibold mb-4">
                Tecnología Innovadora
              </p>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Conoce a Jane, tu Examinadora AI
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Practica el speaking con nuestra examinadora virtual impulsada por inteligencia artificial. Jane te guiará a través de conversaciones reales de examen, proporcionándote feedback personalizado.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-4 h-4 text-green-600" />
                  </div>
                  Avatar realista con voz natural en inglés
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-4 h-4 text-green-600" />
                  </div>
                  Conversaciones interactivas como en el examen real
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-4 h-4 text-green-600" />
                  </div>
                  Feedback inmediato sobre tu pronunciación
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/auth?mode=register')}
                data-testid="avatar-cta-btn"
                className="bg-orange-600 text-white hover:bg-orange-500 rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105"
              >
                Probar Speaking con Jane
                <Mic className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div className="animate-fade-in-up">
              <div className="flex justify-center mb-4">
                <Award className="w-10 h-10 text-orange-500" />
              </div>
              <p className="text-4xl font-bold mb-2">4 Niveles</p>
              <p className="text-slate-400">B1, B2, C1 y C2</p>
            </div>
            <div className="animate-fade-in-up animation-delay-100">
              <div className="flex justify-center mb-4">
                <BookOpen className="w-10 h-10 text-orange-500" />
              </div>
              <p className="text-4xl font-bold mb-2">100+ Ejercicios</p>
              <p className="text-slate-400">Contenido auténtico</p>
            </div>
            <div className="animate-fade-in-up animation-delay-200">
              <div className="flex justify-center mb-4">
                <TrendingUp className="w-10 h-10 text-orange-500" />
              </div>
              <p className="text-4xl font-bold mb-2">Progreso Real</p>
              <p className="text-slate-400">Estadísticas detalladas</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
            ¿Listo para certificar tu inglés?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Únete a miles de profesionales de habla hispana que preparan sus exámenes Cambridge con nosotros.
          </p>
          <Button 
            onClick={() => navigate('/auth?mode=register')}
            data-testid="cta-register-btn"
            className="bg-orange-600 text-white hover:bg-orange-500 rounded-full px-10 py-6 text-lg font-medium transition-all hover:scale-105"
          >
            Crear Cuenta Gratis
          </Button>
        </div>
      </section>

      {/* Premium Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Crown className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Plan Premium
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Accede a ejercicios avanzados, feedback de IA ilimitado, audio real en listening y práctica de speaking con voz.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <div className="bg-white/10 backdrop-blur p-6 rounded-sm border border-white/20">
              <p className="text-3xl font-bold mb-1">€9.99<span className="text-lg font-normal">/mes</span></p>
              <p className="text-slate-400 text-sm">Plan Mensual</p>
            </div>
            <div className="bg-amber-500/20 backdrop-blur p-6 rounded-sm border border-amber-400/40 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                AHORRA 20%
              </span>
              <p className="text-3xl font-bold mb-1">€95.99<span className="text-lg font-normal">/año</span></p>
              <p className="text-slate-400 text-sm">Plan Anual</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/auth?mode=register')}
            data-testid="premium-cta-btn"
            className="mt-8 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-full px-10 py-6 text-lg font-semibold transition-all hover:scale-105"
          >
            Comenzar Prueba Gratis
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Exam Ready English" className="h-8 w-auto" />
          </div>
          <p className="text-sm text-slate-500">
            © 2024 Exam Ready English - Diseñado para profesionales de habla hispana
          </p>
        </div>
      </footer>
    </div>
  );
}
